import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, Circle, Lock, PlayCircle } from "lucide-react";
import { useApi, useApiMutation } from "@/hooks/use-api";

type LearnerContentCapability = "page_content" | "video_source" | "pdf_asset" | "quiz_config";
type LearnerContentReadinessStatus = "ready" | "partial" | "missing" | "not_required";

interface LearnerPortalLesson {
  id: string;
  title: string;
  lessonType: string;
  sortOrder: number;
  estimatedMinutes: number | null;
  completed: boolean;
  completedAt: string | null;
  contentReadiness?: {
    status: LearnerContentReadinessStatus;
    hasContentPage: boolean;
    contentPageReady: boolean;
    hasVideoSource: boolean;
    hasPdf: boolean;
    hasQuiz: boolean;
    requiredCapabilities: LearnerContentCapability[];
    missingCapabilities: LearnerContentCapability[];
  };
}

interface LearnerPortalProgressSummary {
  completedLessons: number;
  totalLessons: number;
  remainingLessons: number;
  completedEstimatedMinutes: number;
  totalEstimatedMinutes: number;
  progressPercent: number;
}

interface LearnerPortalModule {
  id: string;
  title: string;
  sortOrder: number;
  lessons: LearnerPortalLesson[];
  progressSummary: LearnerPortalProgressSummary;
}

interface LearnerPortalCourse {
  enrollment: {
    id: string;
    status: string;
    progressPercent: number;
    enrolledAt: string;
    completedAt: string | null;
  };
  course: {
    id: string;
    slug: string;
    title: string;
    description: string | null;
    status: string;
    publishedAt: string | null;
  };
  modules: LearnerPortalModule[];
  progressSummary: LearnerPortalProgressSummary;
  learnerNextActionPlan: {
    planVersion: "stero.learner-next-action.v1";
    state: "continue_learning" | "review_completed_course" | "waiting_for_content";
    label: string;
    summary: string;
    primaryAction: {
      type: "open_lesson" | "review_course" | "none";
      lessonId: string | null;
      lessonTitle: string | null;
      moduleTitle: string | null;
    };
    safety: {
      learnerServingAllowed: false;
      certificateIssueEnabled: false;
      publicCutoverAllowed: false;
      englishEasePrimary: true;
      steroShadow: true;
    };
  };
  learnerMilestoneMap: {
    mapVersion: "stero.course-milestone-map.v1";
    totalMilestones: number;
    completedMilestones: number;
    currentMilestoneId: string | null;
    milestones: Array<{
      id: string;
      title: string;
      sortOrder: number;
      state: "completed" | "in_progress" | "locked_waiting" | "not_started";
      completedLessons: number;
      totalLessons: number;
      remainingLessons: number;
      progressPercent: number;
      nextLesson: {
        id: string;
        title: string;
        lessonType: string;
      } | null;
    }>;
    safety: {
      learnerServingAllowed: false;
      publicCutoverAllowed: false;
      certificateIssueEnabled: false;
      englishEasePrimary: true;
      steroShadow: true;
    };
  };
  learnerActionSurface: {
    surfaceVersion: "stero.learner-action-surface.v1";
    mode: "stero_shadow_dual_run";
    actions: Array<{
      id: "continue_lesson" | "lesson_progress" | "quiz_practice" | "exercise_practice" | "certificate_readiness";
      label: string;
      category: "navigation" | "progress" | "assessment" | "certificate";
      state: "available_shadow" | "read_only_shadow" | "blocked_shadow" | "not_applicable";
      summary: string;
      availableCount: number;
      blockedReason: string | null;
      targetLesson: {
        id: string;
        title: string;
        moduleTitle: string;
      } | null;
    }>;
    safety: {
      learnerServingAllowed: false;
      publicCutoverAllowed: false;
      certificateIssueEnabled: false;
      quizSubmitEnabled: false;
      exerciseSubmitEnabled: false;
      englishEasePrimary: true;
      steroShadow: true;
    };
  };
  learnerContentReadinessMap: {
    mapVersion: "stero.course-content-readiness-map.v1";
    summary: {
      status: "ready_shadow" | "partial_shadow" | "missing_content";
      readyLessons: number;
      partialLessons: number;
      missingLessons: number;
      notRequiredLessons: number;
      totalLessons: number;
    };
    lessons: Array<{
      id: string;
      title: string;
      moduleId: string;
      moduleTitle: string;
      lessonType: string;
      status: LearnerContentReadinessStatus;
      requiredCapabilities: LearnerContentCapability[];
      missingCapabilities: LearnerContentCapability[];
    }>;
    safety: {
      learnerServingAllowed: false;
      publicCutoverAllowed: false;
      englishEasePrimary: true;
      steroShadow: true;
    };
  };
  learnerProgressEvidence: {
    evidenceVersion: "stero.learner-progress-evidence.v1";
    mode: "stero_shadow_observation";
    summary: {
      state: "completed" | "active_progress" | "not_started" | "no_lessons";
      progressPercent: number;
      completedLessons: number;
      totalLessons: number;
      remainingLessons: number;
      latestActivityAt: string | null;
      inactiveDays: number | null;
    };
    signals: {
      learnerActivityEvents: number;
      lessonStartedEvents: number;
      lessonCompletedEvents: number;
      quizAttemptedEvents: number;
      exerciseSubmittedEvents: number;
      courseCompletedEvents: number;
      completionRecords: number;
      quizAttemptRecords: number;
    };
    health: {
      status: "completed_shadow" | "healthy_progress" | "stalled_shadow" | "not_started_shadow" | "empty_course_shadow";
      risk: "none" | "watch" | "nudge";
      reasons: string[];
    };
    safety: {
      rawPayloadVisible: false;
      learnerServingAllowed: false;
      publicCutoverAllowed: false;
      englishEasePrimary: true;
      steroShadow: true;
    };
  };
  learnerAchievementPath: {
    pathVersion: "stero.learner-achievement-path.v1";
    mode: "read_only_shadow";
    completion: {
      status: "completed" | "in_progress" | "not_started" | "empty_course";
      progressPercent: number;
      completedLessons: number;
      totalLessons: number;
      remainingLessons: number;
      completedAt: string | null;
      courseCompletionObserved: boolean;
    };
    certificate: {
      status: "eligible_shadow" | "not_eligible_shadow" | "issue_blocked_shadow";
      tokenPresent: boolean;
      issueEnabled: false;
      publicVerificationEnabled: false;
      blockedReason: "certificate_issue_gate_off";
    };
    requirements: Array<{
      id: "complete_lessons" | "course_completion_evidence" | "certificate_issue_gate";
      label: string;
      status: "met" | "pending" | "blocked";
      completed: number;
      required: number;
    }>;
    nextStep: {
      type: "continue_lesson" | "review_course" | "wait_for_certificate_gate" | "add_lessons";
      label: string;
      lessonId: string | null;
    };
    safety: {
      learnerServingAllowed: false;
      publicCutoverAllowed: false;
      certificateIssueEnabled: false;
      englishEasePrimary: true;
      steroShadow: true;
    };
  };
  learnerCutoverReadiness: {
    readinessVersion: "stero.learner-portal-cutover-readiness.v1";
    mode: "stero_shadow_dual_run";
    decision: "ready_for_shadow_observation" | "blocked_empty_course" | "blocked_content_readiness";
    shadowObservationReady: boolean;
    publicCutoverReady: false;
    summary: string;
    checks: Array<{
      id:
        | "course_spine"
        | "content_readiness"
        | "action_surface"
        | "progress_evidence"
        | "achievement_path"
        | "public_cutover_gate"
        | "certificate_issue_gate"
        | "submit_gates";
      label: string;
      status: "met" | "pending" | "blocked";
      severity: "blocker" | "guardrail" | "notice";
      observed: string;
      blocksShadow: boolean;
    }>;
    safety: {
      learnerServingAllowed: false;
      publicCutoverAllowed: false;
      parityClaim: "blocked";
      cutoverClaim: "blocked";
      englishEasePrimary: true;
      steroShadow: true;
    };
  };
  learnerOperationsSummary: {
    status: "shadow_ready" | "content_incomplete";
    lessonTypeCounts: {
      total: number;
      video: number;
      text: number;
      quiz: number;
      exercise: number;
      pdf: number;
    };
    pageContentReadiness: {
      requiredLessons: number;
      readyLessons: number;
      missingLessons: number;
    };
    learnerOwnedWrites: {
      lessonActivity: "blocked" | "available_start_only";
      lessonCompletion: "blocked" | "available_lesson_only";
      quizSubmit: "blocked" | "available_attempt_only";
      exerciseSubmit: "blocked";
      certificateIssue: "blocked";
    };
    blockedActions: string[];
    englishEasePrimary: true;
    steroShadow: true;
  };
  learnerDashboardSummary: {
    completionStatus: "in_progress" | "completed";
    nextAction: "continue_next_lesson" | "review_completed_course";
    quizLessonCount: number;
    quizAttemptCount: number;
    exerciseLessonCount: number;
    exerciseContentReadyCount: number;
    exerciseSubmitEnabled: false;
    exerciseProgressWriteEnabled: false;
    certificateEligible: boolean;
    certificateIssueEnabled: false;
    publicServingAllowed: false;
  };
  nextLesson: {
    id: string;
    title: string;
    lessonType: string;
    moduleId: string;
    moduleTitle: string;
  } | null;
}

interface LearnerPortalLessonNavigationItem {
  id: string;
  title: string;
  lessonType: string;
  moduleId: string;
  moduleTitle: string;
}

interface LearnerPortalCatalogResponse {
  routeId: string;
  readOnly: true;
  learnerPortalVersion: string;
  audience: string;
  servingMode: string;
  writeBehavior: "none";
  learnerPortalShadow: {
    mode: "learner_portal_shadow";
    decision: "READY_FOR_LEARNER_PORTAL_SHADOW_READ";
    readPath: "enabled";
    learnerServingAllowed: false;
    publicCutoverAllowed: false;
    parityClaim: "blocked";
    cutoverClaim: "blocked";
    writes: {
      lessonActivity: "blocked" | "available_start_only";
      lessonCompletion: "blocked" | "available_lesson_only";
      courseCompletion: "blocked" | "available_final_lesson_only";
      quizSubmit: "blocked" | "available_attempt_only";
      exerciseSubmit: "blocked" | "available_submission_event_only";
      certificateIssue: "blocked";
    };
  };
  learnerPortalSnapshot?: {
    snapshotVersion: "stero.learner-portal-snapshot.v1";
    mode: "stero_shadow_dual_run";
    generatedAt: string;
    courseCount: number;
    shadowReadyCourses: number;
    shadowBlockedCourses: number;
    publicCutoverReadyCourses: 0;
    activeContracts: string[];
    safety: {
      learnerServingAllowed: false;
      publicCutoverAllowed: false;
      englishEasePrimary: true;
      steroShadow: true;
    };
  };
  counts: {
    enrollments: number;
    courses: number;
    active: number;
    completed: number;
  };
  blockedActions: string[];
  courses: LearnerPortalCourse[];
}

type LearnerPortalCourseResponse = Omit<LearnerPortalCatalogResponse, "counts" | "courses"> & LearnerPortalCourse;
type LearnerPortalLessonWriteBehavior =
  | "none"
  | "lesson_activity_shadow_action_available"
  | "lesson_completion_shadow_action_available"
  | "lesson_and_course_completion_shadow_actions_available"
  | "quiz_submit_shadow_action_available"
  | "exercise_submit_shadow_action_available";

interface LearnerPortalLessonPlayback {
  status: "available" | "unavailable" | "not_video";
  embedUrl: string | null;
  provider: string | null;
  reason?: string | null;
  playbackCapability: "canonical_embed_only" | "unavailable" | "not_applicable";
  signedPlaybackEnabled: false;
  sourceVisible: false;
}

interface LearnerPortalQuizPreview {
  status: "available" | "not_configured";
  submitEnabled: false;
  answerRevealEnabled: false;
  questionCount: number;
  passingScore: number | null;
  maxAttempts: number | null;
  timeLimitMinutes: number | null;
  shuffleQuestions?: boolean;
  shuffleOptions?: boolean;
  questions: Array<{
    id: string;
    type: "multiple_choice" | "true_false";
    text: string;
    points: number;
    options: Array<{
      key: string;
      text: string;
    }>;
  }>;
}

interface LearnerPortalQuizAttemptSummary {
  status: "available" | "not_applicable";
  learnerOwned: true;
  attemptCount: number;
  latestAttempt: {
    attemptNumber: number;
    score: number;
    totalPoints: number;
    passed: boolean;
    submittedAt: string | null;
  } | null;
  bestScore: {
    score: number;
    totalPoints: number;
  } | null;
  answersVisible: false;
  correctAnswersVisible: false;
  explanationsVisible: false;
}

interface LearnerPortalQuizSubmitResponse {
  data?: {
    routeId: "stero.courses.learner-portal.quiz-submit-write.v1";
    decision: "QUIZ_ATTEMPT_RECORDED";
    attemptNumber: number;
    score: number;
    totalPoints: number;
    passed: boolean;
    learnerOwnedScope: true;
    writeBehavior: "quiz_attempt_only";
    answerRevealEnabled: false;
    explanationRevealEnabled: false;
    correctAnswersVisible: false;
    certificateIssueBlocked: true;
    parityClaim: "blocked";
    cutoverClaim: "blocked";
  };
}

interface LearnerPortalExerciseRuntime {
  status: "available" | "unavailable" | "not_applicable";
  mode: "learner_safe_exercise_read" | "not_applicable";
  pageType: string | null;
  blockCount: number;
  pageContentAvailable: boolean;
  requiredSubmission: boolean;
  submitEnabled: false;
  progressWriteEnabled: false;
  uploadEnabled: false;
  reviewQueueEnabled: false;
  submitBlockedReason: "exercise_submit_gate_off" | null;
  progressBlockedReason: "exercise_progress_gate_off" | null;
  uploadBlockedReason: "exercise_upload_pipeline_not_mounted" | null;
  exerciseBlocks: Array<{
    index: number;
    title: string;
    instructions: string | null;
    acceptedFileTypes: string[];
    maxFileSizeMb: number;
    required: boolean;
    rubric: string | null;
  }>;
}

interface LearnerPortalCertificateEligibility {
  status: "eligible" | "not_eligible";
  learnerOwned: true;
  courseCompleted: boolean;
  completedLessons: number;
  totalLessons: number;
  progressPercent: number;
  completedAt: string | null;
  certificateTokenPresent: boolean;
  issueEnabled: false;
  issueBlockedReason: "certificate_issue_gate_off";
  publicVerificationEnabled: false;
}

interface LearnerPortalActivityTimeline {
  status: "available" | "empty";
  learnerOwned: true;
  rawPayloadVisible: false;
  sourceTables: string[];
  items: Array<{
    kind: "lesson_started" | "lesson_completed" | "quiz_attempted" | "quiz_passed" | "quiz_failed" | "exercise_submitted" | "course_completed";
    label: string;
    occurredAt: string;
    score: number | null;
    totalPoints: number | null;
    passed: boolean | null;
    source: string;
  }>;
}

interface LearnerPortalLessonReadResponse extends Omit<LearnerPortalCatalogResponse, "counts" | "courses" | "writeBehavior"> {
  rawSourceVisible: false;
  writeBehavior: LearnerPortalLessonWriteBehavior;
  allowedActions?: string[];
  dualRunStatus?: {
    mode: "stero_shadow_observation";
    learnerServingAllowed: false;
    publicCutoverAllowed: false;
    learnerOwnedActionsAvailable: boolean;
    writeBehavior: LearnerPortalLessonWriteBehavior;
    allowedActions: string[];
    blockedActions: string[];
    parityClaim: "blocked";
    cutoverClaim: "blocked";
  };
  lessonActivityAction?: {
    status: "available" | "blocked";
    enabled: boolean;
    endpointPath: string | null;
    confirmationRequired: "learner-portal-lesson-activity-write" | null;
    blockedReason: "feature_flag_off" | "enrollment_not_active" | null;
  };
  lessonCompletionAction?: {
    status: "available" | "blocked";
    enabled: boolean;
    endpointPath: string | null;
    confirmationRequired: "learner-portal-lesson-completion-write" | null;
    blockedReason: "feature_flag_off" | "lesson_already_completed" | "enrollment_not_active" | "course_completion_gate_required" | null;
    courseCompletionWriteBlocked: boolean;
    courseCompletionWriteMode?: "final_lesson_completes_course" | null;
    certificateIssueBlocked: true;
    quizSubmitBlocked: true;
  };
  quizSubmitAction?: {
    status: "available" | "blocked";
    enabled: boolean;
    endpointPath: string | null;
    confirmationRequired: "learner-portal-quiz-submit-write" | null;
    blockedReason: "feature_flag_off" | "enrollment_not_active" | "quiz_not_configured" | null;
    answerRevealEnabled: false;
    explanationRevealEnabled: false;
    correctAnswersVisible: false;
  };
  exerciseSubmitAction?: {
    status: "available" | "blocked";
    enabled: boolean;
    endpointPath: string | null;
    confirmationRequired: "learner-portal-exercise-submit-write" | null;
    blockedReason: "feature_flag_off" | "not_exercise_lesson" | "enrollment_not_active" | "exercise_runtime_not_ready" | null;
    uploadEnabled: false;
    reviewQueueEnabled: false;
    progressWriteEnabled: false;
  };
  enrollment: Pick<LearnerPortalCourse["enrollment"], "id" | "status">;
  course: Pick<LearnerPortalCourse["course"], "id" | "slug" | "title" | "status">;
  certificateEligibility?: LearnerPortalCertificateEligibility;
  learnerActivityTimeline?: LearnerPortalActivityTimeline;
  nextLesson: LearnerPortalCourse["nextLesson"];
  lessonNavigation?: {
    previousLesson: LearnerPortalLessonNavigationItem | null;
    nextLesson: LearnerPortalLessonNavigationItem | null;
  };
  lesson: LearnerPortalLesson & {
    module: {
      id: string;
      title: string;
      sortOrder: number;
    };
    page: {
      id: string;
      slug: string | null;
      title: string | null;
      pageType: string | null;
      status: string | null;
    } | null;
    assets: {
      videoPlayback: LearnerPortalLessonPlayback;
      hasPdf: boolean;
      hasQuiz: boolean;
    };
    content: {
      status: "available" | "unavailable";
      mode: "sanitized_published_page_text";
      reason: string | null;
      rawPageDataVisible: false;
      sourceVisible: false;
      pageVersionNumber: number | null;
      sections: Array<{
        index: number;
        type: string;
        title: string | null;
        body: string[];
      }>;
    };
    quizPreview?: LearnerPortalQuizPreview;
    quizAttemptSummary?: LearnerPortalQuizAttemptSummary;
    exerciseRuntime?: LearnerPortalExerciseRuntime;
  };
}

interface LearnerPortalLessonCompletionWriteResult {
  nextLesson?: LearnerPortalCourse["nextLesson"];
  courseCompleted?: boolean;
}

interface LearnerPortalLessonCompletionWriteResponse {
  data?: LearnerPortalLessonCompletionWriteResult;
}

function formatProgressSummary(summary: LearnerPortalProgressSummary) {
  const lessonLabel = summary.totalLessons === 1 ? "lesson" : "lessons";
  const pieces = [`${summary.completedLessons}/${summary.totalLessons} ${lessonLabel}`];
  if (summary.totalEstimatedMinutes > 0) {
    pieces.push(`${summary.completedEstimatedMinutes}/${summary.totalEstimatedMinutes} min`);
  }
  return pieces.join(" - ");
}

function formatLessonMeta(lesson: LearnerPortalLesson) {
  const pieces = [lesson.lessonType];
  if (lesson.estimatedMinutes !== null) {
    pieces.push(`${lesson.estimatedMinutes} min`);
  }
  return pieces.join(" - ");
}

function ProgressBar({ value }: { value: number }) {
  const bounded = Math.min(Math.max(value, 0), 100);
  return (
    <div className="h-2 rounded-full bg-[var(--color-surface)]">
      <div
        className="h-full rounded-full bg-[var(--color-primary)]"
        style={{ width: `${bounded}%` }}
      />
    </div>
  );
}

function LearnerDashboardSummaryCard({ course }: { course: LearnerPortalCourse }) {
  const summary = course.learnerDashboardSummary;

  return (
    <div className="grid gap-3 md:grid-cols-5">
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Next action</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">
          {summary.nextAction === "continue_next_lesson" ? "Continue learning" : "Review course"}
        </p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Quiz activity</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">
          {summary.quizAttemptCount}/{summary.quizLessonCount} attempts
        </p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Exercise readiness</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">
          {summary.exerciseContentReadyCount}/{summary.exerciseLessonCount} ready
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          {summary.exerciseSubmitEnabled || summary.exerciseProgressWriteEnabled ? "Writes mounted" : "Writes blocked"}
        </p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Certificate</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">
          {summary.certificateEligible ? "Eligible" : "Not ready"}
        </p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Serving mode</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">
          {summary.publicServingAllowed ? "Public" : "Shadow"}
        </p>
      </div>
    </div>
  );
}

function LearnerNextActionPlanCard({
  courseRef,
  plan,
}: {
  courseRef: string;
  plan: LearnerPortalCourse["learnerNextActionPlan"];
}) {
  const lessonPath = plan.primaryAction.type === "open_lesson" && plan.primaryAction.lessonId
    ? `/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(plan.primaryAction.lessonId)}`
    : null;

  return (
    <section className="rounded-[28px] border border-[var(--color-primary)]/30 bg-[var(--color-background)] p-6 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Stero next action
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">{plan.label}</h2>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{plan.summary}</p>
          <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
            Shadow mode: certificates, public serving, and cutover stay blocked.
          </p>
        </div>
        {lessonPath ? (
          <Link
            to={lessonPath}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--color-primary)] px-5 py-3 text-sm font-semibold text-[var(--color-primary-foreground)] transition hover:opacity-90"
          >
            <PlayCircle className="h-4 w-4" />
            Open next lesson
          </Link>
        ) : (
          <span className="inline-flex items-center justify-center rounded-full border border-[var(--color-border)] px-5 py-3 text-sm font-semibold text-[var(--color-foreground)]">
            {plan.primaryAction.type === "review_course" ? "Course complete" : "Waiting"}
          </span>
        )}
      </div>
    </section>
  );
}

function formatMilestoneState(state: LearnerPortalCourse["learnerMilestoneMap"]["milestones"][number]["state"]) {
  if (state === "completed") {
    return "Completed";
  }
  if (state === "in_progress") {
    return "In progress";
  }
  if (state === "locked_waiting") {
    return "Waiting";
  }
  return "Not started";
}

function MilestoneStateIcon({ state }: { state: LearnerPortalCourse["learnerMilestoneMap"]["milestones"][number]["state"] }) {
  if (state === "completed") {
    return <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />;
  }
  if (state === "in_progress") {
    return <PlayCircle className="h-5 w-5 text-[var(--color-primary)]" />;
  }
  if (state === "locked_waiting") {
    return <Lock className="h-5 w-5 text-[var(--color-muted-foreground)]" />;
  }
  return <Circle className="h-5 w-5 text-[var(--color-muted-foreground)]" />;
}

function CourseMilestoneMapPanel({
  courseRef,
  milestoneMap,
}: {
  courseRef: string;
  milestoneMap: LearnerPortalCourse["learnerMilestoneMap"];
}) {
  if (milestoneMap.totalMilestones === 0) {
    return null;
  }

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Course milestone map
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            {milestoneMap.completedMilestones}/{milestoneMap.totalMilestones} milestones complete
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Reusable Stero LMS sequence for modules, progress, and next lesson routing. English Ease remains primary while this runs in shadow.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          Shadow map
        </span>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-2">
        {milestoneMap.milestones.map((milestone) => {
          const nextLessonPath = milestone.state === "in_progress" && milestone.nextLesson
            ? `/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(milestone.nextLesson.id)}`
            : null;

          return (
            <article key={milestone.id} className="rounded-2xl bg-[var(--color-surface)] p-4">
              <div className="flex items-start gap-3">
                <MilestoneStateIcon state={milestone.state} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="font-semibold text-[var(--color-foreground)]">{milestone.title}</h3>
                      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                        {milestone.completedLessons}/{milestone.totalLessons} lessons complete · {milestone.remainingLessons} remaining
                      </p>
                    </div>
                    <span className="w-fit rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
                      {formatMilestoneState(milestone.state)}
                    </span>
                  </div>

                  <div className="mt-3">
                    <ProgressBar value={milestone.progressPercent} />
                  </div>

                  {nextLessonPath ? (
                    <Link
                      to={nextLessonPath}
                      className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-primary)] hover:opacity-80"
                    >
                      Next: {milestone.nextLesson?.title}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : (
                    <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                      {milestone.state === "locked_waiting" ? "Queued after the current milestone." : "No learner action needed here."}
                    </p>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function CourseModuleBottleneckPanel({
  courseRef,
  modules,
}: {
  courseRef: string;
  modules: LearnerPortalModule[];
}) {
  const bottlenecks = modules
    .map((module) => {
      const missingLessons = module.lessons.filter((lesson) => (
        lesson.contentReadiness?.status === "missing" || lesson.contentReadiness?.status === "partial"
      ));
      return {
        module,
        missingLessons,
        score: module.progressSummary.remainingLessons + missingLessons.length,
      };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);

  if (bottlenecks.length === 0) {
    return null;
  }

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Module bottlenecks
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            Modules that need learner or content progress
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Prioritized by remaining learner lessons and missing learner-safe content. This keeps course-builder work separate while making the reusable LMS spine observable.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {bottlenecks.length} active
        </span>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-2">
        {bottlenecks.map(({ module, missingLessons }) => {
          const firstLesson = module.lessons.find((lesson) => !lesson.completed) ?? missingLessons[0] ?? null;
          const targetPath = firstLesson
            ? `/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(firstLesson.id)}`
            : null;

          return (
            <article key={module.id} className="rounded-2xl bg-[var(--color-surface)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-[var(--color-foreground)]">{module.title}</h3>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    {module.progressSummary.remainingLessons} learner lessons remaining · {missingLessons.length} content readiness issues
                  </p>
                </div>
                <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs font-semibold text-[var(--color-foreground)]">
                  {module.progressSummary.progressPercent}%
                </span>
              </div>
              <div className="mt-3">
                <ProgressBar value={module.progressSummary.progressPercent} />
              </div>
              {targetPath ? (
                <Link
                  to={targetPath}
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-primary)] hover:opacity-80"
                >
                  Open next module lesson
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function formatLearnerActionState(state: LearnerPortalCourse["learnerActionSurface"]["actions"][number]["state"]) {
  if (state === "available_shadow") {
    return "Available";
  }
  if (state === "read_only_shadow") {
    return "Read-only";
  }
  if (state === "blocked_shadow") {
    return "Blocked";
  }
  return "Not applicable";
}

function LearnerActionSurfacePanel({
  courseRef,
  surface,
}: {
  courseRef: string;
  surface: LearnerPortalCourse["learnerActionSurface"];
}) {
  if (surface.actions.length === 0) {
    return null;
  }

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Learner action surface
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            Reusable LMS actions are mapped for shadow observation
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Navigation and learner-safe reads can be observed in Stero. Submissions, certificates, and public cutover remain disabled until their gates are intentionally opened.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {surface.mode.replaceAll("_", " ")}
        </span>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        {surface.actions.map((action) => {
          const lessonPath = action.targetLesson
            ? `/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(action.targetLesson.id)}`
            : null;

          return (
            <article key={action.id} className="rounded-2xl bg-[var(--color-surface)] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs capitalize text-[var(--color-muted-foreground)]">{action.category}</p>
                  <h3 className="mt-1 font-semibold text-[var(--color-foreground)]">{action.label}</h3>
                </div>
                <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs font-semibold text-[var(--color-foreground)]">
                  {formatLearnerActionState(action.state)}
                </span>
              </div>
              <p className="mt-3 text-sm leading-6 text-[var(--color-muted-foreground)]">{action.summary}</p>
              <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                Count: {action.availableCount}
                {action.blockedReason ? ` · ${action.blockedReason.replaceAll("_", " ")}` : ""}
              </p>
              {lessonPath ? (
                <Link
                  to={lessonPath}
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-primary)] hover:opacity-80"
                >
                  Open lesson
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : null}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function formatContentCapability(capability: LearnerContentCapability) {
  return capability.replaceAll("_", " ");
}

function formatContentReadinessStatus(status: LearnerContentReadinessStatus) {
  if (status === "ready") {
    return "Ready";
  }
  if (status === "partial") {
    return "Partial";
  }
  if (status === "missing") {
    return "Missing";
  }
  return "Optional";
}

function CourseContentReadinessPanel({
  courseRef,
  readinessMap,
}: {
  courseRef: string;
  readinessMap: LearnerPortalCourse["learnerContentReadinessMap"];
}) {
  const lessonsNeedingWork = readinessMap.lessons.filter((lesson) => (
    lesson.status === "missing" || lesson.status === "partial"
  ));

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Course content readiness
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            {readinessMap.summary.readyLessons}/{readinessMap.summary.totalLessons} lessons ready for shadow reads
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            This maps page content, quiz config, PDFs, and video sources without approving public serving. Builder work stays in the course builder; Stero only exposes learner-safe readiness here.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {readinessMap.summary.status.replaceAll("_", " ")}
        </span>
      </div>

      <dl className="mt-5 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Ready</dt>
          <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{readinessMap.summary.readyLessons}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Partial</dt>
          <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{readinessMap.summary.partialLessons}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Missing</dt>
          <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{readinessMap.summary.missingLessons}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Optional/no gate</dt>
          <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{readinessMap.summary.notRequiredLessons}</dd>
        </div>
      </dl>

      {lessonsNeedingWork.length > 0 ? (
        <div className="mt-5 grid gap-3 lg:grid-cols-2">
          {lessonsNeedingWork.slice(0, 6).map((lesson) => (
            <Link
              key={lesson.id}
              to={`/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(lesson.id)}`}
              className="rounded-2xl bg-[var(--color-surface)] p-4 transition hover:bg-[var(--color-surface)]/80"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs text-[var(--color-muted-foreground)]">{lesson.moduleTitle} - {lesson.lessonType}</p>
                  <h3 className="mt-1 font-semibold text-[var(--color-foreground)]">{lesson.title}</h3>
                </div>
                <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs font-semibold text-[var(--color-foreground)]">
                  {formatContentReadinessStatus(lesson.status)}
                </span>
              </div>
              <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                Missing: {lesson.missingCapabilities.length > 0
                  ? lesson.missingCapabilities.map(formatContentCapability).join(", ")
                  : "none"}
              </p>
            </Link>
          ))}
        </div>
      ) : (
        <p className="mt-5 rounded-2xl bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
          No content readiness blockers are visible in the shadow map.
        </p>
      )}
    </section>
  );
}

function formatActivityDate(value: string | null) {
  if (!value) {
    return "No activity";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return date.toLocaleString();
}

function LearnerProgressEvidencePanel({ evidence }: { evidence: LearnerPortalCourse["learnerProgressEvidence"] }) {
  const signals = evidence.signals;

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Learner progress evidence
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            {evidence.summary.completedLessons}/{evidence.summary.totalLessons} lessons complete in Stero shadow
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Aggregated starts, completions, quiz attempts, and exercise submissions. Raw event payloads stay hidden while English Ease remains primary.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {evidence.health.status.replaceAll("_", " ")}
        </span>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Latest activity</p>
          <p className="mt-1 font-semibold text-[var(--color-foreground)]">{formatActivityDate(evidence.summary.latestActivityAt)}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {evidence.summary.inactiveDays === null ? "No inactivity score" : `${evidence.summary.inactiveDays} days inactive`}
          </p>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Lesson events</p>
          <p className="mt-1 font-semibold text-[var(--color-foreground)]">
            {signals.lessonStartedEvents} starts · {signals.lessonCompletedEvents} completions
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{signals.completionRecords} completion records</p>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Assessment events</p>
          <p className="mt-1 font-semibold text-[var(--color-foreground)]">
            {signals.quizAttemptRecords} quiz records
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{signals.quizAttemptedEvents} quiz events · {signals.exerciseSubmittedEvents} exercise events</p>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Risk</p>
          <p className="mt-1 font-semibold text-[var(--color-foreground)]">{evidence.health.risk}</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{signals.learnerActivityEvents} domain events observed</p>
        </div>
      </div>

      {evidence.health.reasons.length > 0 ? (
        <ul className="mt-5 grid gap-2 text-sm text-[var(--color-muted-foreground)]">
          {evidence.health.reasons.map((reason) => (
            <li key={reason} className="rounded-2xl bg-[var(--color-surface)] px-4 py-3">
              {reason}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function LearnerAchievementPathPanel({
  courseRef,
  path,
}: {
  courseRef: string;
  path: LearnerPortalCourse["learnerAchievementPath"];
}) {
  const nextLessonPath = path.nextStep.lessonId
    ? `/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(path.nextStep.lessonId)}`
    : null;

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Achievement path
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            {path.completion.status.replaceAll("_", " ")}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Read-only achievement progress for the reusable Stero LMS spine. Certificates remain gated until the approved issuer flow is mounted.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {path.certificate.status.replaceAll("_", " ")}
        </span>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Completion</p>
          <p className="mt-1 font-semibold text-[var(--color-foreground)]">
            {path.completion.completedLessons}/{path.completion.totalLessons} lessons
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {path.completion.progressPercent}% complete · {path.completion.remainingLessons} remaining
          </p>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Completion evidence</p>
          <p className="mt-1 font-semibold text-[var(--color-foreground)]">
            {path.completion.courseCompletionObserved ? "Observed" : "Pending"}
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {path.completion.completedAt ? formatActivityDate(path.completion.completedAt) : "No completion timestamp"}
          </p>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <p className="text-xs text-[var(--color-muted-foreground)]">Certificate issuer</p>
          <p className="mt-1 font-semibold text-[var(--color-foreground)]">
            {path.certificate.issueEnabled ? "Mounted" : "Blocked"}
          </p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Token {path.certificate.tokenPresent ? "present" : "not present"} · {path.certificate.blockedReason.replaceAll("_", " ")}
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-3">
        {path.requirements.map((requirement) => (
          <article key={requirement.id} className="rounded-2xl bg-[var(--color-surface)] p-4">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-semibold text-[var(--color-foreground)]">{requirement.label}</h3>
              <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs font-semibold text-[var(--color-foreground)]">
                {requirement.status}
              </span>
            </div>
            <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
              {requirement.completed}/{requirement.required}
            </p>
          </article>
        ))}
      </div>

      <div className="mt-5 rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-sm font-semibold text-[var(--color-foreground)]">{path.nextStep.label}</p>
        {nextLessonPath ? (
          <Link
            to={nextLessonPath}
            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-primary)] hover:opacity-80"
          >
            Continue lesson
            <ArrowRight className="h-4 w-4" />
          </Link>
        ) : null}
      </div>
    </section>
  );
}

function LearnerCutoverReadinessPanel({ readiness }: { readiness: LearnerPortalCourse["learnerCutoverReadiness"] }) {
  const shadowBlockers = readiness.checks.filter((check) => check.blocksShadow);

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Learner portal cutover readiness
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            {readiness.shadowObservationReady ? "Ready for Stero shadow observation" : "Shadow blockers remain"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">{readiness.summary}</p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {readiness.decision.replaceAll("_", " ")}
        </span>
      </div>

      <dl className="mt-5 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Shadow ready</dt>
          <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{readiness.shadowObservationReady ? "Yes" : "No"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Public cutover</dt>
          <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{readiness.publicCutoverReady ? "Ready" : "Blocked"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Shadow blockers</dt>
          <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{shadowBlockers.length}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Cutover claim</dt>
          <dd className="mt-1 font-semibold text-[var(--color-foreground)]">{readiness.safety.cutoverClaim}</dd>
        </div>
      </dl>

      <div className="mt-5 grid gap-3 lg:grid-cols-2">
        {readiness.checks.map((check) => (
          <article key={check.id} className="rounded-2xl bg-[var(--color-surface)] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-[var(--color-foreground)]">{check.label}</h3>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{check.observed}</p>
              </div>
              <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-xs font-semibold text-[var(--color-foreground)]">
                {check.status}
              </span>
            </div>
            <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
              {check.severity}{check.blocksShadow ? " · blocks shadow" : " · does not block shadow"}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

function CatalogCourseHealthCard({ course }: { course: LearnerPortalCourse }) {
  const cutover = course.learnerCutoverReadiness;
  const evidence = course.learnerProgressEvidence;
  const readiness = course.learnerContentReadinessMap.summary;

  return (
    <div className="mt-5 grid gap-3 text-sm md:grid-cols-4">
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Shadow readiness</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">
          {cutover.shadowObservationReady ? "Ready" : "Blocked"}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          {cutover.decision.replaceAll("_", " ")}
        </p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Content map</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">
          {readiness.readyLessons}/{readiness.totalLessons} ready
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          {readiness.missingLessons} missing · {readiness.partialLessons} partial
        </p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Achievement</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">
          {course.learnerAchievementPath.completion.status.replaceAll("_", " ")}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          Certificate {course.learnerAchievementPath.certificate.status.replaceAll("_", " ")}
        </p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Evidence</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">{evidence.health.risk}</p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          {evidence.summary.inactiveDays === null ? "No activity age" : `${evidence.summary.inactiveDays} days inactive`}
        </p>
      </div>
    </div>
  );
}

function LessonContentReadinessBadge({ lesson }: { lesson: LearnerPortalLesson }) {
  const readiness = lesson.contentReadiness;
  if (!readiness) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
      <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 font-semibold text-[var(--color-foreground)]">
        {formatContentReadinessStatus(readiness.status)}
      </span>
      {readiness.missingCapabilities.length > 0 ? (
        <span className="text-[var(--color-muted-foreground)]">
          Missing {readiness.missingCapabilities.map(formatContentCapability).join(", ")}
        </span>
      ) : (
        <span className="text-[var(--color-muted-foreground)]">
          Learner-safe content mapped
        </span>
      )}
    </div>
  );
}

function LearnerPortalSnapshotPanel({ snapshot }: { snapshot: LearnerPortalCatalogResponse["learnerPortalSnapshot"] }) {
  if (!snapshot) {
    return null;
  }

  return (
    <div className="mt-5 grid gap-3 text-sm md:grid-cols-4">
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Contracts</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">{snapshot.activeContracts.length} active</p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Shadow ready</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">{snapshot.shadowReadyCourses}/{snapshot.courseCount}</p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Shadow blocked</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">{snapshot.shadowBlockedCourses}</p>
      </div>
      <div className="rounded-2xl bg-[var(--color-surface)] p-4">
        <p className="text-xs text-[var(--color-muted-foreground)]">Public cutover</p>
        <p className="mt-1 font-semibold text-[var(--color-foreground)]">{snapshot.publicCutoverReadyCourses} ready</p>
      </div>
    </div>
  );
}

function LearnerOperationsSummaryPanel({ course }: { course: LearnerPortalCourse }) {
  const operations = course.learnerOperationsSummary;
  const readiness = operations.pageContentReadiness;
  const types = operations.lessonTypeCounts;

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            LMS operations
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            {operations.status === "shadow_ready" ? "Course is ready for shadow observation" : "Course content still needs attention"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            English Ease remains primary while Stero observes the reusable course spine. Learner-owned writes and public cutover remain gated.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {operations.steroShadow ? "Stero shadow" : "Shadow off"}
        </span>
      </div>
      <dl className="mt-5 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Lesson mix</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {types.total} total
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {types.video} video · {types.text} text · {types.quiz} quiz · {types.exercise} exercise · {types.pdf} pdf
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Page content</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {readiness.readyLessons}/{readiness.requiredLessons} ready
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {readiness.missingLessons} missing
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Writes</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            Lesson {operations.learnerOwnedWrites.lessonCompletion.replaceAll("_", " ")}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Quiz {operations.learnerOwnedWrites.quizSubmit.replaceAll("_", " ")} · Exercise {operations.learnerOwnedWrites.exerciseSubmit}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Blocked actions</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {operations.blockedActions.length}
          </dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {operations.englishEasePrimary ? "English Ease primary" : "Primary switched"}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[28px] border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-8 text-center">
      <BookOpen className="mx-auto h-8 w-8 text-[var(--color-muted-foreground)]" />
      <h2 className="mt-4 text-lg font-semibold text-[var(--color-foreground)]">No active courses yet</h2>
      <p className="mx-auto mt-2 max-w-xl text-sm text-[var(--color-muted-foreground)]">
        Your published enrolled courses will appear here once your provider enables learner access for this portal.
      </p>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4">
      {[0, 1, 2].map((item) => (
        <div key={item} className="h-32 animate-pulse rounded-[28px] bg-[var(--color-background)]" />
      ))}
    </div>
  );
}

function GateUnavailable({ message }: { message: string }) {
  return (
    <div className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-3">
          <Lock className="h-5 w-5 text-[var(--color-muted-foreground)]" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Course portal is not enabled yet</h2>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}

function LessonPlaybackPanel({ lesson, playback }: { lesson: LearnerPortalLessonReadResponse["lesson"]; playback: LearnerPortalLessonPlayback | null | undefined }) {
  if (playback?.status === "available" && playback.embedUrl) {
    return (
      <section className="overflow-hidden rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)]">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] px-4 py-3 text-xs">
          <span className="font-semibold text-[var(--color-foreground)]">Lesson video</span>
          {playback.provider ? (
            <span className="rounded-full bg-[var(--color-surface)] px-2 py-1 text-[var(--color-muted-foreground)]">
              {playback.provider}
            </span>
          ) : null}
          <span className="rounded-full bg-[var(--color-surface)] px-2 py-1 text-[var(--color-muted-foreground)]">
            {playback.playbackCapability.replaceAll("_", " ")}
          </span>
        </div>
        <div className="aspect-video bg-black">
          <iframe
            src={playback.embedUrl}
            title={`${lesson.title} video`}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="h-full w-full border-0"
          />
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-[28px] border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex items-start gap-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-3">
          <PlayCircle className="h-5 w-5 text-[var(--color-muted-foreground)]" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Video playback is unavailable</h2>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            {playback?.reason ?? "This lesson is not serving video playback for learners yet."}
          </p>
        </div>
      </div>
    </section>
  );
}

function LessonAssetSummary({ data }: { data: LearnerPortalLessonReadResponse }) {
  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Lesson details</h2>
      <dl className="mt-4 grid gap-3 text-sm md:grid-cols-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Linked page</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{data.lesson.page?.title ?? "Not linked"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">PDF</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{data.lesson.assets.hasPdf ? "Available" : "Unavailable"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Quiz</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{data.lesson.assets.hasQuiz ? "Included" : "Not included"}</dd>
        </div>
      </dl>
    </section>
  );
}

function LessonQuizPreviewPanel({
  quizPreview,
  quizSubmitAction,
  allowedActions,
  onSubmitted,
}: {
  quizPreview: LearnerPortalQuizPreview | null | undefined;
  quizSubmitAction?: LearnerPortalLessonReadResponse["quizSubmitAction"];
  allowedActions?: string[];
  onSubmitted: () => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const mutation = useApiMutation<
    { confirmation: "learner-portal-quiz-submit-write"; answers: Record<string, string> },
    LearnerPortalQuizSubmitResponse
  >(quizSubmitAction?.endpointPath ?? "");

  if (!quizPreview || quizPreview.status !== "available" || quizPreview.questions.length === 0) return null;
  const submitMounted = quizSubmitAction?.enabled === true;
  const canSubmitQuiz = submitMounted
    && quizSubmitAction?.status === "available"
    && !!quizSubmitAction.endpointPath
    && quizSubmitAction.confirmationRequired === "learner-portal-quiz-submit-write"
    && quizSubmitAction.answerRevealEnabled === false
    && quizSubmitAction.explanationRevealEnabled === false
    && quizSubmitAction.correctAnswersVisible === false
    && allowedActions?.includes("quiz_submit") === true;
  const requiredQuestionCount = quizPreview.questions.length;
  const selectedAnswerCount = quizPreview.questions.filter((question) => answers[question.id]).length;
  const latestAttempt = mutation.data?.data;

  function setAnswer(questionId: string, optionKey: string) {
    setAnswers((current) => ({ ...current, [questionId]: optionKey }));
    setFormError(null);
  }

  async function submitQuiz(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmitQuiz) return;
    if (selectedAnswerCount !== requiredQuestionCount) {
      setFormError("Answer every question before submitting this shadow quiz attempt.");
      return;
    }

    const result = await mutation.mutate({
      confirmation: "learner-portal-quiz-submit-write",
      answers,
    });
    if (result) {
      setAnswers({});
      onSubmitted();
    }
  }

  return (
    <form onSubmit={submitQuiz} className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">Quiz preview</p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">Learner-safe quiz questions</h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Questions and options are visible for learning context. Answer reveal and explanations remain blocked; quiz attempts mount only through the gated Stero write route.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {quizPreview.questionCount} questions
        </span>
      </div>
      <dl className="mt-5 grid gap-3 text-sm md:grid-cols-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Passing score</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{quizPreview.passingScore ?? "Not set"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Max attempts</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{quizPreview.maxAttempts === 0 ? "Unlimited" : quizPreview.maxAttempts ?? "Not set"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Submit capability</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{submitMounted ? "Gated route mounted" : "Blocked"}</dd>
          {quizSubmitAction?.endpointPath ? (
            <dd className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">{quizSubmitAction.endpointPath}</dd>
          ) : null}
        </div>
      </dl>
      {canSubmitQuiz ? (
        <div className="mt-5 rounded-2xl border border-[var(--color-primary)]/30 bg-[var(--color-primary)]/5 p-4 text-sm">
          <p className="font-semibold text-[var(--color-foreground)]">Shadow quiz submission is enabled</p>
          <p className="mt-1 text-[var(--color-muted-foreground)]">
            This records a learner-owned quiz attempt only. Correct answers, explanations, certificates, public serving, and cutover remain blocked.
          </p>
          <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
            {selectedAnswerCount}/{requiredQuestionCount} answers selected
          </p>
        </div>
      ) : null}
      <div className="mt-5 space-y-4">
        {quizPreview.questions.map((question, questionIndex) => (
          <article key={question.id} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="font-semibold text-[var(--color-foreground)]">
                {questionIndex + 1}. {question.text}
              </h3>
              <span className="rounded-full bg-[var(--color-background)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                {question.points} pts
              </span>
            </div>
            {question.options.length > 0 ? (
              canSubmitQuiz ? (
                <fieldset className="mt-4 grid gap-2 text-sm text-[var(--color-muted-foreground)]">
                  <legend className="sr-only">Answer for {question.text}</legend>
                  {question.options.map((option) => (
                    <label
                      key={option.key}
                      className="flex cursor-pointer items-center gap-3 rounded-xl bg-[var(--color-background)] px-3 py-2 transition hover:ring-1 hover:ring-[var(--color-primary)]/30"
                    >
                      <input
                        type="radio"
                        name={`quiz-${question.id}`}
                        value={option.key}
                        checked={answers[question.id] === option.key}
                        onChange={() => setAnswer(question.id, option.key)}
                        disabled={mutation.isLoading}
                        className="h-4 w-4"
                      />
                      <span>{option.text}</span>
                    </label>
                  ))}
                </fieldset>
              ) : (
                <ul className="mt-4 grid gap-2 text-sm text-[var(--color-muted-foreground)]">
                  {question.options.map((option) => (
                    <li key={option.key} className="rounded-xl bg-[var(--color-background)] px-3 py-2">
                      {option.text}
                    </li>
                  ))}
                </ul>
              )
            ) : null}
          </article>
        ))}
      </div>
      {canSubmitQuiz ? (
        <div className="mt-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-sm">
            {formError ? <p className="text-[var(--color-destructive)]">{formError}</p> : null}
            {mutation.error ? <p className="text-[var(--color-destructive)]">{mutation.error}</p> : null}
            {latestAttempt ? (
              <p className="text-[var(--color-foreground)]">
                Attempt {latestAttempt.attemptNumber} recorded: {latestAttempt.score}/{latestAttempt.totalPoints} ({latestAttempt.passed ? "passed" : "not passed"}).
              </p>
            ) : null}
          </div>
          <button
            type="submit"
            disabled={mutation.isLoading || selectedAnswerCount !== requiredQuestionCount}
            className="rounded-full bg-[var(--color-primary)] px-5 py-3 text-sm font-semibold text-[var(--color-primary-foreground)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isLoading ? "Submitting..." : "Submit quiz attempt"}
          </button>
        </div>
      ) : null}
    </form>
  );
}

function LessonQuizAttemptSummaryPanel({
  summary,
}: {
  summary: LearnerPortalQuizAttemptSummary | null | undefined;
}) {
  if (!summary || summary.status !== "available" || summary.attemptCount === 0) return null;

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Quiz attempts
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            Your attempt history is learner-owned
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Stero can show attempt outcomes without exposing answer keys, explanations, or raw grading data.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {summary.attemptCount} attempts
        </span>
      </div>
      <dl className="mt-5 grid gap-3 text-sm md:grid-cols-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Latest attempt</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {summary.latestAttempt
              ? `${summary.latestAttempt.score}/${summary.latestAttempt.totalPoints} (${summary.latestAttempt.passed ? "passed" : "not passed"})`
              : "No score"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Best score</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {summary.bestScore ? `${summary.bestScore.score}/${summary.bestScore.totalPoints}` : "No score"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Answer visibility</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {summary.correctAnswersVisible || summary.explanationsVisible ? "Visible" : "Blocked"}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function CertificateEligibilityPanel({
  eligibility,
}: {
  eligibility: LearnerPortalCertificateEligibility | null | undefined;
}) {
  if (!eligibility) return null;

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Certificate boundary
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            {eligibility.status === "eligible" ? "Eligible, issuer still gated" : "Not eligible yet"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Stero exposes certificate readiness as learner-owned read evidence. Issuing certificates and public verification stay blocked until a separate approved write flow is mounted.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {eligibility.progressPercent}% complete
        </span>
      </div>
      <dl className="mt-5 grid gap-3 text-sm md:grid-cols-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Lessons completed</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {eligibility.completedLessons}/{eligibility.totalLessons}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Issuer</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {eligibility.issueEnabled ? "Mounted" : "Blocked"}
          </dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Verification</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">
            {eligibility.publicVerificationEnabled ? "Public" : "Private only"}
          </dd>
        </div>
      </dl>
    </section>
  );
}

function LessonDualRunStatusPanel({ data }: { data: LearnerPortalLessonReadResponse }) {
  const status = data.dualRunStatus;
  const actions = status?.allowedActions ?? data.allowedActions ?? [];
  const writeBehavior = status?.writeBehavior ?? data.writeBehavior;

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Stero shadow dual-run
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            {actions.length > 0 ? "Learner-owned shadow actions mounted" : "Observation only"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            This lesson is served from the reusable Stero learner portal contract. Public cutover and parity claims remain blocked while learner-owned actions are observed behind gates.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {writeBehavior.replaceAll("_", " ")}
        </span>
      </div>
      <dl className="mt-5 grid gap-3 text-sm md:grid-cols-3">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Learner serving</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">No public cutover</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Allowed shadow actions</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{actions.length}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Cutover claim</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{status?.cutoverClaim ?? "blocked"}</dd>
        </div>
      </dl>
    </section>
  );
}

function LessonActivityTimelinePanel({
  timeline,
}: {
  timeline: LearnerPortalActivityTimeline | null | undefined;
}) {
  if (!timeline) return null;

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Learner timeline
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            Observed LMS activity
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            This combines learner-owned starts, completions, quiz outcomes, and course completion evidence without exposing raw event payloads.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          Safe summary
        </span>
      </div>
      {timeline.items.length > 0 ? (
        <ol className="mt-5 space-y-3">
          {timeline.items.map((item, index) => (
            <li key={`${item.kind}-${item.occurredAt}-${index}`} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-[var(--color-foreground)]">{item.label}</p>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    {new Date(item.occurredAt).toLocaleString()} · {item.source.replaceAll("_", " ")}
                  </p>
                </div>
                {item.score !== null && item.totalPoints !== null ? (
                  <span className="rounded-full bg-[var(--color-background)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                    {item.score}/{item.totalPoints}
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-5 rounded-2xl bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
          No learner activity has been observed for this lesson yet.
        </p>
      )}
    </section>
  );
}

function LessonContentPanel({ content }: { content: LearnerPortalLessonReadResponse["lesson"]["content"] }) {
  if (content.status !== "available" || content.sections.length === 0) {
    return (
      <section className="rounded-[28px] border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Lesson content</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          {content.reason === "missing_published_page"
            ? "This lesson does not have a published learner page yet."
            : content.reason === "missing_current_version"
              ? "The linked learner page has no current published content version yet."
              : "No learner-safe text sections are available for this lesson yet."}
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Lesson content</h2>
        <span className="rounded-full bg-[var(--color-surface)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]">
          Safe text view
        </span>
      </div>
      <div className="mt-5 space-y-4">
        {content.sections.map((section) => (
          <article key={`${section.index}-${section.type}`} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
            {section.title ? (
              <h3 className="text-base font-semibold text-[var(--color-foreground)]">{section.title}</h3>
            ) : null}
            {section.body.length > 0 ? (
              <div className="mt-3 space-y-2 text-sm leading-6 text-[var(--color-muted-foreground)]">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function LessonExerciseRuntimePanel({
  runtime,
  exerciseSubmitAction,
}: {
  runtime: LearnerPortalExerciseRuntime | null | undefined;
  exerciseSubmitAction?: LearnerPortalLessonReadResponse["exerciseSubmitAction"];
}) {
  if (!runtime || runtime.status === "not_applicable") return null;
  const submitBlockedReason = exerciseSubmitAction?.blockedReason ?? runtime.submitBlockedReason;

  if (runtime.status !== "available") {
    return (
      <section className="rounded-[28px] border border-dashed border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-[var(--color-surface)] p-3">
            <Lock className="h-5 w-5 text-[var(--color-muted-foreground)]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Exercise runtime is not ready</h2>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              This exercise still needs learner-safe published page content before Stero can observe it in shadow mode.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Exercise runtime
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">
            Learner-safe exercise view
          </h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Stero can read exercise instructions from published course exercise pages. Submission, upload, review, and progress writes stay blocked until separate gated flows are mounted.
          </p>
        </div>
        <span className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-xs font-semibold text-[var(--color-foreground)]">
          {runtime.blockCount} structured blocks
        </span>
      </div>
      <dl className="mt-5 grid gap-3 text-sm md:grid-cols-4">
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Page type</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{runtime.pageType ?? "Not tagged"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Required</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{runtime.requiredSubmission ? "Yes" : "No"}</dd>
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Submit</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{exerciseSubmitAction?.enabled ? "Gated route mounted" : "Blocked"}</dd>
          <dd className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {submitBlockedReason?.replaceAll("_", " ") ?? "not mounted"}
          </dd>
          {exerciseSubmitAction?.endpointPath ? (
            <dd className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">{exerciseSubmitAction.endpointPath}</dd>
          ) : null}
        </div>
        <div className="rounded-2xl bg-[var(--color-surface)] p-4">
          <dt className="text-xs text-[var(--color-muted-foreground)]">Progress write</dt>
          <dd className="mt-1 font-medium text-[var(--color-foreground)]">{runtime.progressWriteEnabled ? "Mounted" : "Blocked"}</dd>
        </div>
      </dl>
      {runtime.exerciseBlocks.length > 0 ? (
        <div className="mt-5 space-y-4">
          {runtime.exerciseBlocks.map((block) => (
            <article key={`${block.index}-${block.title}`} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="font-semibold text-[var(--color-foreground)]">{block.title}</h3>
                <span className="rounded-full bg-[var(--color-background)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                  {block.required ? "Required" : "Optional"}
                </span>
              </div>
              {block.instructions ? (
                <p className="mt-3 text-sm leading-6 text-[var(--color-muted-foreground)]">{block.instructions}</p>
              ) : null}
              {block.rubric ? (
                <p className="mt-3 rounded-xl bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
                  Rubric: {block.rubric}
                </p>
              ) : null}
              <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">
                Accepted files: {block.acceptedFileTypes.length > 0 ? block.acceptedFileTypes.join(", ") : "Not specified"} · Max {block.maxFileSizeMb} MB
              </p>
            </article>
          ))}
        </div>
      ) : (
        <p className="mt-5 rounded-2xl bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
          Published exercise page content is available, but no structured exercise block was found. Stero still serves the safe lesson content above while submit/runtime writes stay blocked.
        </p>
      )}
    </section>
  );
}

function LessonActivityPanel({ data }: { data: LearnerPortalLessonReadResponse }) {
  const action = data.lessonActivityAction;
  const canRecordStart = action?.status === "available"
    && action.enabled === true
    && action.endpointPath
    && action.confirmationRequired === "learner-portal-lesson-activity-write"
    && data.allowedActions?.includes("lesson_activity_write") === true;
  const mutation = useApiMutation<{ confirmation: "learner-portal-lesson-activity-write" }, { data?: unknown }>(
    action?.endpointPath ?? "",
  );

  async function recordStart() {
    if (!canRecordStart) return;
    await mutation.mutate({ confirmation: "learner-portal-lesson-activity-write" });
  }

  if (!canRecordStart) return null;

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Lesson activity</h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            Record a learner-owned lesson start event for progress analytics. This does not complete the lesson, submit quizzes, issue certificates, or serve learners publicly.
          </p>
          {mutation.error ? (
            <p className="mt-2 text-sm text-[var(--color-destructive)]">{mutation.error}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={recordStart}
          disabled={mutation.isLoading}
          className="rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-5 py-3 text-sm font-semibold text-[var(--color-foreground)] transition hover:border-[var(--color-primary)]/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isLoading ? "Recording..." : "Record lesson start"}
        </button>
      </div>
    </section>
  );
}

function LessonCompletionPanel({
  data,
  onCompleted,
}: {
  data: LearnerPortalLessonReadResponse;
  onCompleted: (result: LearnerPortalLessonCompletionWriteResult) => void;
}) {
  const action = data.lessonCompletionAction;
  const canComplete = action?.status === "available"
    && action.enabled === true
    && action.endpointPath
    && action.confirmationRequired === "learner-portal-lesson-completion-write"
    && (
      action.courseCompletionWriteBlocked === true
      || action.courseCompletionWriteMode === "final_lesson_completes_course"
    )
    && action.certificateIssueBlocked === true
    && action.quizSubmitBlocked === true
    && data.allowedActions?.includes("lesson_completion_write") === true;
  const mutation = useApiMutation<
    { confirmation: "learner-portal-lesson-completion-write" },
    LearnerPortalLessonCompletionWriteResponse
  >(
    action?.endpointPath ?? "",
  );

  async function completeLesson() {
    if (!canComplete) return;
    const result = await mutation.mutate({ confirmation: "learner-portal-lesson-completion-write" });
    if (result) onCompleted(result.data ?? {});
  }

  if (!canComplete) {
    const reason = action?.blockedReason === "lesson_already_completed"
      ? "This lesson is already complete."
      : action?.blockedReason === "enrollment_not_active"
        ? "Your enrollment is not active for lesson completion."
        : action?.blockedReason === "course_completion_gate_required"
          ? "This final lesson needs course completion to be enabled before it can be closed."
        : "Lesson completion is not enabled for this portal yet.";

    return (
      <div className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-sm text-[var(--color-muted-foreground)]">
        <p>{reason}</p>
        <p className="mt-2">
          Quiz submission, certificates, and course completion remain blocked until separate approved backend flows are enabled.
        </p>
      </div>
    );
  }

  return (
    <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Lesson progress</h2>
          <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
            {action?.courseCompletionWriteMode === "final_lesson_completes_course"
              ? "Mark this final lesson complete and close the course for your enrolled learner record. Quiz submission and certificates stay blocked."
              : "Mark this lesson complete for your enrolled learner record. Course completion, quiz submission, and certificates stay blocked."}
          </p>
          {mutation.error ? (
            <p className="mt-2 text-sm text-[var(--color-destructive)]">{mutation.error}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={completeLesson}
          disabled={mutation.isLoading}
          className="rounded-full bg-[var(--color-primary)] px-5 py-3 text-sm font-semibold text-[var(--color-primary-foreground)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isLoading
            ? "Saving..."
            : action?.courseCompletionWriteMode === "final_lesson_completes_course"
              ? "Complete course"
              : "Mark lesson complete"}
        </button>
      </div>
    </section>
  );
}

function ContinueLessonCard({
  courseRef,
  nextLesson,
  courseCompleted = false,
}: {
  courseRef: string;
  nextLesson: LearnerPortalCourse["nextLesson"];
  courseCompleted?: boolean;
}) {
  if (!nextLesson) {
    return (
      <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-2xl bg-[var(--color-surface)] p-3">
            <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">
              {courseCompleted ? "Course completion recorded" : "You are caught up"}
            </h2>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              {courseCompleted
                ? "Stero recorded the course completion in shadow mode. Certificate issuing and public cutover remain gated."
                : "No next lesson is available for this enrolled course yet."}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-[28px] border border-[var(--color-primary)]/30 bg-[var(--color-background)] p-6 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">
            Continue learning
          </p>
          <h2 className="mt-2 text-lg font-semibold text-[var(--color-foreground)]">{nextLesson.title}</h2>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            {nextLesson.moduleTitle} - {nextLesson.lessonType}
          </p>
        </div>
        <Link
          to={`/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(nextLesson.id)}`}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--color-primary)] px-5 py-3 text-sm font-semibold text-[var(--color-primary-foreground)] transition hover:opacity-90"
        >
          <PlayCircle className="h-4 w-4" />
          Open next lesson
        </Link>
      </div>
    </section>
  );
}

function LessonNavigationPanel({
  courseRef,
  navigation,
}: {
  courseRef: string;
  navigation: LearnerPortalLessonReadResponse["lessonNavigation"] | undefined;
}) {
  if (!navigation?.previousLesson && !navigation?.nextLesson) return null;

  const linkClass = "flex min-h-24 flex-1 items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 transition hover:border-[var(--color-primary)]/40";

  return (
    <nav className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-4" aria-label="Lesson navigation">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
        Lesson navigation
      </p>
      <div className="grid gap-3 md:grid-cols-2">
        {navigation.previousLesson ? (
          <Link
            to={`/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(navigation.previousLesson.id)}`}
            className={linkClass}
          >
            <ArrowLeft className="h-5 w-5 shrink-0 text-[var(--color-primary)]" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Previous lesson</p>
              <p className="mt-1 font-medium text-[var(--color-foreground)]">{navigation.previousLesson.title}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {navigation.previousLesson.moduleTitle} - {navigation.previousLesson.lessonType}
              </p>
            </div>
          </Link>
        ) : (
          <div className="min-h-24 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
            This is the first lesson in the course.
          </div>
        )}

        {navigation.nextLesson ? (
          <Link
            to={`/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(navigation.nextLesson.id)}`}
            className={`${linkClass} md:justify-end md:text-right`}
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">Next lesson</p>
              <p className="mt-1 font-medium text-[var(--color-foreground)]">{navigation.nextLesson.title}</p>
              <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                {navigation.nextLesson.moduleTitle} - {navigation.nextLesson.lessonType}
              </p>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-[var(--color-primary)]" />
          </Link>
        ) : (
          <div className="min-h-24 rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)] md:text-right">
            This is the final lesson in the course.
          </div>
        )}
      </div>
    </nav>
  );
}

function CatalogView() {
  const { data, isLoading, error } = useApi<LearnerPortalCatalogResponse>("/v1/portal/courses");

  return (
    <div className="space-y-8">
      <header className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
        <p className="text-sm font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Learning portal</p>
        <h1 className="mt-2 text-3xl font-semibold text-[var(--color-foreground)]">Your courses</h1>
        <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
          Read-only access to the courses your account is enrolled in. Course progress and quiz submissions are still managed by approved backend flows.
        </p>
        {!isLoading && !error && data ? (
          <LearnerPortalSnapshotPanel snapshot={data.learnerPortalSnapshot} />
        ) : null}
      </header>

      {isLoading ? <LoadingState /> : null}
      {!isLoading && error ? <GateUnavailable message={error} /> : null}
      {!isLoading && !error && data?.courses.length === 0 ? <EmptyState /> : null}

      {!isLoading && !error && data ? (
        <div className="grid gap-4">
          {data.courses.map((item) => (
            <Link
              key={item.course.id}
              to={`/portal/courses/${encodeURIComponent(item.course.slug)}`}
              className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6 transition hover:border-[var(--color-primary)]/40 hover:shadow-sm"
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]">{item.enrollment.status}</p>
                  <h2 className="mt-2 text-xl font-semibold text-[var(--color-foreground)]">{item.course.title}</h2>
                  <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{item.course.description ?? "Course structure and progress are available."}</p>
                </div>
                <div className="min-w-44 rounded-2xl bg-[var(--color-surface)] p-4">
                  <p className="text-sm font-medium text-[var(--color-foreground)]">{item.progressSummary.progressPercent}% complete</p>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                    {formatProgressSummary(item.progressSummary)}
                  </p>
                  <div className="mt-3">
                    <ProgressBar value={item.progressSummary.progressPercent} />
                  </div>
                </div>
              </div>
              {item.learnerNextActionPlan.state !== "waiting_for_content" ? (
                <div className="mt-5 flex items-center gap-2 rounded-2xl bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-foreground)]">
                  <PlayCircle className="h-4 w-4 text-[var(--color-primary)]" />
                  {item.learnerNextActionPlan.label}: {item.learnerNextActionPlan.primaryAction.lessonTitle ?? item.course.title}
                </div>
              ) : null}
              <CatalogCourseHealthCard course={item} />
              <div className="mt-5">
                <LearnerDashboardSummaryCard course={item} />
              </div>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DetailView({ courseRef }: { courseRef: string }) {
  const { data, isLoading, error } = useApi<LearnerPortalCourseResponse>(`/v1/portal/courses/${encodeURIComponent(courseRef)}`);

  return (
    <div className="space-y-8">
      <Link to="/portal/courses" className="text-sm font-medium text-[var(--color-primary)] hover:opacity-80">
        Back to courses
      </Link>

      {isLoading && !data ? <LoadingState /> : null}
      {!data && error ? <GateUnavailable message={error} /> : null}

      {data ? (
        <>
          <header className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">{data.course.slug}</p>
            <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-[var(--color-foreground)]">{data.course.title}</h1>
                <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
                  {data.course.description ?? "Course structure and progress are available."}
                </p>
              </div>
              <div className="min-w-48 rounded-2xl bg-[var(--color-surface)] p-4">
                <p className="text-sm font-medium text-[var(--color-foreground)]">{data.progressSummary.progressPercent}% complete</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  {formatProgressSummary(data.progressSummary)}
                </p>
                <div className="mt-3">
                  <ProgressBar value={data.progressSummary.progressPercent} />
                </div>
              </div>
            </div>
          </header>

          <LearnerNextActionPlanCard courseRef={courseRef} plan={data.learnerNextActionPlan} />
          <CourseMilestoneMapPanel courseRef={courseRef} milestoneMap={data.learnerMilestoneMap} />
          <CourseModuleBottleneckPanel courseRef={courseRef} modules={data.modules} />
          <LearnerActionSurfacePanel courseRef={courseRef} surface={data.learnerActionSurface} />
          <CourseContentReadinessPanel courseRef={courseRef} readinessMap={data.learnerContentReadinessMap} />
          <LearnerProgressEvidencePanel evidence={data.learnerProgressEvidence} />
          <LearnerAchievementPathPanel courseRef={courseRef} path={data.learnerAchievementPath} />
          <LearnerCutoverReadinessPanel readiness={data.learnerCutoverReadiness} />
          <LearnerDashboardSummaryCard course={data} />
          <LearnerOperationsSummaryPanel course={data} />

          <section className="space-y-4">
            {data.modules.map((module) => (
              <article key={module.id} className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-[var(--color-foreground)]">{module.title}</h2>
                    <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                      {formatProgressSummary(module.progressSummary)}
                    </p>
                  </div>
                  <div className="min-w-40">
                    <span className="text-xs text-[var(--color-muted-foreground)]">
                      {module.progressSummary.progressPercent}% complete
                    </span>
                    <div className="mt-2">
                      <ProgressBar value={module.progressSummary.progressPercent} />
                    </div>
                  </div>
                </div>
                <div className="mt-4 divide-y divide-[var(--color-border)]">
                  {module.lessons.map((lesson) => (
                    <Link
                      key={lesson.id}
                      to={`/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(lesson.id)}`}
                      className="flex flex-col gap-2 py-4 transition hover:bg-[var(--color-surface)] sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex items-start gap-3">
                        {lesson.completed ? (
                          <CheckCircle2 className="mt-0.5 h-5 w-5 text-[var(--color-success)]" />
                        ) : (
                          <Circle className="mt-0.5 h-5 w-5 text-[var(--color-muted-foreground)]" />
                        )}
                        <div>
                          <p className="font-medium text-[var(--color-foreground)]">{lesson.title}</p>
                          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{formatLessonMeta(lesson)}</p>
                          <LessonContentReadinessBadge lesson={lesson} />
                        </div>
                      </div>
                      <span className="text-xs font-medium uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                        {lesson.completed ? "Complete" : "Not started"}
                      </span>
                    </Link>
                  ))}
                </div>
              </article>
            ))}
          </section>

          <div className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-sm text-[var(--color-muted-foreground)]">
            This portal view is read-only. Lesson completion, quiz submission, certificates, and public course serving remain blocked until separate approved backend flows are enabled.
          </div>
        </>
      ) : null}
    </div>
  );
}

function LessonReadView({ courseRef, lessonRef }: { courseRef: string; lessonRef: string }) {
  const { data, isLoading, error, refetch } = useApi<LearnerPortalLessonReadResponse>(
    `/v1/portal/courses/${encodeURIComponent(courseRef)}/lessons/${encodeURIComponent(lessonRef)}`,
  );
  const [completionHandoff, setCompletionHandoff] = useState<{
    lessonRef: string;
    nextLesson: LearnerPortalCourse["nextLesson"];
    courseCompleted: boolean;
  } | null>(null);
  const playback = data?.lesson.assets.videoPlayback;
  const completionHandoffForLesson = completionHandoff?.lessonRef === lessonRef ? completionHandoff : null;
  const completionNextLesson = data?.nextLesson ?? completionHandoffForLesson?.nextLesson ?? null;
  const completionCourseCompleted = completionHandoffForLesson?.courseCompleted === true
    || data?.certificateEligibility?.courseCompleted === true;
  const showCompletionContinuation = data?.lesson.completed === true || completionHandoffForLesson !== null;

  return (
    <div className="space-y-8">
      <Link to={`/portal/courses/${encodeURIComponent(courseRef)}`} className="text-sm font-medium text-[var(--color-primary)] hover:opacity-80">
        Back to course
      </Link>

      {isLoading && !data ? <LoadingState /> : null}
      {!data && error ? <GateUnavailable message={error} /> : null}

      {data ? (
        <>
          <header className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-background)] p-6">
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
              {data.course.title} / {data.lesson.module.title}
            </p>
            <div className="mt-3 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-[var(--color-foreground)]">{data.lesson.title}</h1>
                <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">{formatLessonMeta(data.lesson)}</p>
              </div>
              <div className="rounded-2xl bg-[var(--color-surface)] px-4 py-3 text-xs font-medium uppercase tracking-[0.14em] text-[var(--color-muted-foreground)]">
                {data.dualRunStatus?.learnerOwnedActionsAvailable ? "Shadow actions gated" : "Read-only lesson"}
              </div>
            </div>
          </header>

          <LessonDualRunStatusPanel data={data} />
          <LessonNavigationPanel courseRef={courseRef} navigation={data.lessonNavigation} />
          <LessonActivityTimelinePanel timeline={data.learnerActivityTimeline} />
          <LessonActivityPanel data={data} />
          {data.lesson.lessonType === "video" ? <LessonPlaybackPanel lesson={data.lesson} playback={playback} /> : null}
          <LessonContentPanel content={data.lesson.content} />
          <LessonExerciseRuntimePanel runtime={data.lesson.exerciseRuntime} exerciseSubmitAction={data.exerciseSubmitAction} />
          <LessonQuizPreviewPanel
            quizPreview={data.lesson.quizPreview}
            quizSubmitAction={data.quizSubmitAction}
            allowedActions={data.allowedActions}
            onSubmitted={refetch}
          />
          <LessonQuizAttemptSummaryPanel summary={data.lesson.quizAttemptSummary} />
          <CertificateEligibilityPanel eligibility={data.certificateEligibility} />
          <LessonAssetSummary data={data} />
          <LessonCompletionPanel
            data={data}
            onCompleted={(result) => {
              setCompletionHandoff({
                lessonRef,
                nextLesson: result.nextLesson ?? null,
                courseCompleted: result.courseCompleted === true,
              });
              refetch();
            }}
          />
          {showCompletionContinuation ? (
            <ContinueLessonCard
              courseRef={courseRef}
              nextLesson={completionNextLesson}
              courseCompleted={completionCourseCompleted}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export default function PortalCourses() {
  const { courseRef, lessonRef } = useParams();
  if (courseRef && lessonRef) return <LessonReadView courseRef={courseRef} lessonRef={lessonRef} />;
  return courseRef ? <DetailView courseRef={courseRef} /> : <CatalogView />;
}
